'use client';

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Phone, Mail, MapPin, Send, MessageCircle, Clock, CheckCircle2 } from 'lucide-react';
import { useCommerce } from '@/context/CommerceContext';

export default function ContactModal() {
  const { isContactOpen, setIsContactOpen } = useCommerce();
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({ name: '', phone: '', message: '' });

  if (!isContactOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="relative w-full max-w-2xl bg-[#FFFFFF] rounded-3xl border-2 border-[#E09F55] shadow-2xl p-6 sm:p-8 text-[#171711] my-8"
      >
        <button
          onClick={() => {
            setIsContactOpen(false);
            setSubmitted(false);
          }}
          className="absolute top-4 right-4 p-2 rounded-full bg-white/80 hover:bg-white text-[#171711] transition-colors cursor-pointer"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="mb-6">
          <span className="text-xs font-bold text-[#387613] uppercase tracking-wider block">
            Customer Support & Trade Desk
          </span>
          <h3 className="font-display text-2xl font-bold text-[#950E09]">
            Get in Touch with Elavarasi Foods
          </h3>
          <p className="text-xs text-[#171711]/70 font-sans">
            For retail queries, custom wedding packages, corporate gifting, or wholesale orders.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 items-start font-sans">
          
          {/* Contact Details */}
          <div className="space-y-4 text-xs font-sans">
            <div className="p-4 rounded-2xl bg-white border border-[#DDDAC4] space-y-3">
              <div className="flex items-start space-x-2.5">
                <MapPin className="w-4 h-4 text-[#950E09] shrink-0 mt-0.5" />
                <div>
                  <strong className="block text-[#171711]">Central Kitchen & Logistics:</strong>
                  <span className="text-gray-600">SIDCO Industrial Estate, Madurai & Chennai Highway, Tamil Nadu - 600032</span>
                </div>
              </div>

              <div className="flex items-center space-x-2.5">
                <Phone className="w-4 h-4 text-[#950E09] shrink-0" />
                <div>
                  <strong className="block text-[#171711]">Customer & Order Desk:</strong>
                  <span className="text-gray-600 font-mono">+91 94432 10890 / +91 98401 23456</span>
                </div>
              </div>

              <div className="flex items-center space-x-2.5">
                <Mail className="w-4 h-4 text-[#950E09] shrink-0" />
                <div>
                  <strong className="block text-[#171711]">Commercial Inquiries:</strong>
                  <span className="text-gray-600">sales@elavarasifoods.com</span>
                </div>
              </div>

              <div className="flex items-center space-x-2.5">
                <Clock className="w-4 h-4 text-[#387613] shrink-0" />
                <div>
                  <strong className="block text-[#171711]">Kitchen Operations:</strong>
                  <span className="text-gray-600">Mon – Sat: 07:00 AM – 09:00 PM</span>
                </div>
              </div>
            </div>

            <a
              href="https://wa.me/919443210890?text=Hello%20Elavarasi%20Foods,%20I%20would%20like%20to%20inquire%20about%20your%20traditional%20sweets%20and%20savouries."
              target="_blank"
              rel="noreferrer"
              className="p-3.5 rounded-2xl bg-[#387613] hover:bg-[#1F5B18] text-white font-bold flex items-center justify-center space-x-2 transition-all shadow font-sans"
            >
              <MessageCircle className="w-4 h-4 text-[#F2BC6F]" />
              <span>Direct WhatsApp Order Desk</span>
            </a>
          </div>

          {/* Quick Message Form */}
          <div className="bg-white p-5 rounded-2xl border border-[#DDDAC4] font-sans">
            {submitted ? (
              <div className="text-center py-6 space-y-2">
                <CheckCircle2 className="w-8 h-8 text-[#387613] mx-auto" />
                <h4 className="font-display font-bold text-sm text-[#387613]">Message Received!</h4>
                <p className="text-xs text-gray-600">Our customer relations officer will contact you within 2 hours.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-3 text-xs">
                <div>
                  <label className="font-semibold block mb-1">Your Name *</label>
                  <input
                    type="text"
                    required
                    placeholder="Full Name"
                    value={formData.name}
                    onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
                    className="w-full p-2.5 rounded-xl border border-[#DDDAC4] bg-[#F7F4D6]/30 text-xs focus:outline-none focus:ring-1 focus:ring-[#950E09]"
                  />
                </div>
                <div>
                  <label className="font-semibold block mb-1">Mobile / WhatsApp *</label>
                  <input
                    type="tel"
                    required
                    placeholder="+91 98400 ..."
                    value={formData.phone}
                    onChange={(e) => setFormData(p => ({ ...p, phone: e.target.value }))}
                    className="w-full p-2.5 rounded-xl border border-[#DDDAC4] bg-[#F7F4D6]/30 text-xs focus:outline-none focus:ring-1 focus:ring-[#950E09]"
                  />
                </div>
                <div>
                  <label className="font-semibold block mb-1">Requirement / Question *</label>
                  <textarea
                    rows={3}
                    required
                    placeholder="Mention event date, snacks required, or delivery location..."
                    value={formData.message}
                    onChange={(e) => setFormData(p => ({ ...p, message: e.target.value }))}
                    className="w-full p-2.5 rounded-xl border border-[#DDDAC4] bg-[#F7F4D6]/30 text-xs focus:outline-none focus:ring-1 focus:ring-[#950E09]"
                  />
                </div>
                <button
                  type="submit"
                  className="w-full py-2.5 rounded-xl bg-[#950E09] hover:bg-[#B90505] text-[#F7F4D6] font-bold text-xs uppercase tracking-wider shadow cursor-pointer flex items-center justify-center space-x-1.5"
                >
                  <Send className="w-3.5 h-3.5 text-[#F2BC6F]" />
                  <span>Send Inquiry</span>
                </button>
              </form>
            )}
          </div>

        </div>

      </motion.div>
    </div>
  );
}
